import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  BellRing,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  FileImage,
  FileText,
  Hammer,
  Package,
  Receipt,
  RefreshCw,
  Users,
  Wrench,
} from 'lucide-react-native';
import { ComponentType, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { exportTasksToIcs } from '@/lib/ical';
import { exportBinderPdf } from '@/lib/pdf';
import { listContacts } from '@/lib/contacts';
import { listDocumentsForProperty } from '@/lib/documents';
import { listInventory } from '@/lib/inventory';
import { listNotes } from '@/lib/notes';
import { listQuotes } from '@/lib/quotes';
import { goBackOrTo } from '@/lib/nav';
import {
  getActiveProperty,
  getActiveTasks,
  getServiceHistoryForProperty,
  getSystemsForProperty,
  regenerateTasksForProperty,
  regenerateTasksWithDiff,
  type ReplanDiff,
} from '@/lib/tasks';
import { ReplanSummaryModal } from '@/components/ReplanSummaryModal';

export default function Tools() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Replan preview modal — opens with a dry-run diff; user confirms
  // via "Replan now" to actually persist. Matches the My Home / Edit
  // Home flow exactly.
  const [replanSummary, setReplanSummary] = useState<ReplanDiff | null>(null);
  const [replanLoading, setReplanLoading] = useState(false);
  const [replanHomeName, setReplanHomeName] = useState<string>('this home');
  const [replanPropertyId, setReplanPropertyId] = useState<string | null>(null);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const exportIcs = async () => {
    setBusy('ics');
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return flash('No property set up yet.');
      const tasks = await getActiveTasks(db, prop.id, 500);
      const res = await exportTasksToIcs(tasks, prop.name ?? undefined);
      flash(res.success ? `Exported ${tasks.length} tasks to calendar.` : `Export failed: ${res.error}`);
    } finally {
      setBusy(null);
    }
  };

  const exportPdf = async () => {
    setBusy('pdf');
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return flash('No property set up yet.');
      const [systems, contacts, documents, history, inventory, notesList, quotes] = await Promise.all([
        getSystemsForProperty(db, prop.id),
        listContacts(db, prop.id),
        listDocumentsForProperty(db, prop.id),
        getServiceHistoryForProperty(db, prop.id, 500),
        listInventory(db, prop.id),
        listNotes(db, prop.id, 500),
        listQuotes(db, prop.id),
      ]);
      const res = await exportBinderPdf({
        property: prop,
        systems,
        contacts,
        documents,
        history,
        inventory,
        notes: notesList,
        quotes,
      });
      flash(res.success ? 'Home Record PDF generated.' : `PDF failed: ${res.error}`);
    } finally {
      setBusy(null);
    }
  };

  const replan = async () => {
    setBusy('replan');
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      setReplanHomeName(prop.name?.trim() || 'this home');
      setReplanPropertyId(prop.id);
      setReplanLoading(true);
      setReplanSummary({ added: 0, removed: 0, rescheduled: 0, addedTitles: [], removedTitles: [], rescheduledTitles: [] });
      const { previewReplanDiff } = await import('@/lib/tasks');
      const diff = await previewReplanDiff(db, prop.id);
      setReplanSummary(diff);
      setReplanLoading(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Tools</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Power-user tools</Text>
        <Text style={styles.sub}>
          Export, sync, organize. Everything below pulls from the same Homestead
          data you&apos;ve been building.
        </Text>

        <SectionHeader>Export your home</SectionHeader>
        <Card>
          <ToolRow
            label="Add tasks to my calendar (.ics)"
            sub="Imports into Google / Apple / Outlook. Each task becomes a 30-min event with a 1-day reminder."
            Icon={CalendarDays}
            onPress={exportIcs}
            busy={busy === 'ics'}
          />
          <ToolRow
            label="Generate Home Record PDF"
            sub="Property summary + systems + contacts + service history + docs. Bring to insurance / refi / resale."
            Icon={FileText}
            onPress={exportPdf}
            busy={busy === 'pdf'}
            last
          />
        </Card>

        <SectionHeader>Organize</SectionHeader>
        <Card>
          <ToolRow
            label="Inventory"
            sub="Track high-value items — receipts, serials, photos. Built for insurance + resale."
            Icon={Package}
            onPress={() => router.push('/inventory' as never)}
          />
          <ToolRow
            label="Expenses"
            sub="Utilities, insurance, taxes, projects — with recurring + tax-deductible flags."
            Icon={Receipt}
            onPress={() => router.push('/expenses' as never)}
          />
          <ToolRow
            label="Files"
            sub="Every photo and document you've uploaded — across every system."
            Icon={FileImage}
            onPress={() => router.push('/media' as never)}
          />
          <ToolRow
            label="My contacts"
            sub="Plumber, HVAC, electrician — saved with last-used sort."
            Icon={Users}
            onPress={() => router.push('/contacts' as never)}
          />
          <ToolRow
            label="Find a Pro"
            sub="Your saved pros, Google Maps, Yelp, BBB, plus national chains."
            Icon={Hammer}
            onPress={() => router.push('/pros' as never)}
            last
          />
        </Card>

        <SectionHeader>Tune the plan</SectionHeader>
        <Card>
          <ToolRow
            label="Replan my year"
            sub="Rebuild the task list against today's systems + rules. Keeps your custom tasks."
            Icon={RefreshCw}
            onPress={replan}
            busy={busy === 'replan'}
          />
          <ToolRow
            label="Weekly digest notifications"
            sub="One reminder per week. Native only — web browsers can't schedule."
            Icon={BellRing}
            onPress={() => router.push('/notifications' as never)}
            last
          />
        </Card>

        {toast ? (
          <View style={styles.toast}>
            <Text style={styles.toastText}>{toast}</Text>
          </View>
        ) : null}
      </ScrollView>
      <ReplanSummaryModal
        visible={replanSummary !== null}
        diff={replanSummary}
        loading={replanLoading}
        homeName={replanHomeName}
        onClose={() => {
          setReplanSummary(null);
          setReplanLoading(false);
        }}
        onConfirm={async () => {
          if (!replanPropertyId) return;
          try {
            await regenerateTasksWithDiff(db, replanPropertyId);
          } finally {
            setReplanSummary(null);
            setReplanLoading(false);
          }
        }}
      />
    </SafeAreaView>
  );
}

function SectionHeader({ children }: { children: string }) {
  return <Text style={styles.section}>{children.toUpperCase()}</Text>;
}
function Card({ children }: { children: React.ReactNode }) {
  return <View style={styles.card}>{children}</View>;
}
function ToolRow({
  label,
  sub,
  Icon,
  onPress,
  busy,
  last,
}: {
  label: string;
  sub?: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  onPress: () => void;
  busy?: boolean;
  last?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={busy}
      style={({ pressed }) => [
        styles.row,
        last && { borderBottomWidth: 0 },
        pressed && !busy && { opacity: 0.85 },
      ]}>
      <View style={styles.rowIcon}>
        <Icon size={18} color={Palette.forest} strokeWidth={1.7} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.rowLabel}>{busy ? 'Working…' : label}</Text>
        {sub ? <Text style={styles.rowSub}>{sub}</Text> : null}
      </View>
      <ChevronRight size={18} color={Palette.inkFaint} strokeWidth={1.7} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  section: {
    marginTop: Spacing.five, marginHorizontal: 22,
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkFaint,
    letterSpacing: 0.7, marginBottom: 8,
  },
  card: {
    marginHorizontal: 18, backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, paddingHorizontal: 16,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: Palette.line,
  },
  rowIcon: {
    width: 36, height: 36, borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  rowLabel: { fontSize: 14, fontFamily: Fonts.sansBold, color: Palette.ink },
  rowSub: {
    marginTop: 1, fontSize: 13, fontFamily: Fonts.sansMedium,
    color: Palette.inkSoft, lineHeight: 17,
  },
  toast: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    padding: 13, borderRadius: 12, backgroundColor: Palette.forest,
  },
  toastText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5 },
});
